import React, { FC, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
    StyledMap,
    StyledContentMap,
    StyledMapList,
    StyledSearchWrapper,
    StyledSearchLocation,
    StyledButton,
} from './styled';
import MapHelper from './map-helper/map-helper';
import { StoreListUl } from './components';
import { fitBounds } from './utils/fitBounds';
import { IM82FindDealerMapBlockViewModel, MapZoom } from './models';
import markerSelected from '$assets/pinimerco_black.svg';
import markerOption from '$assets/imerco_pin.svg';
import markerUser from '$assets/marker-user.svg';
import { generateMapMarkers } from './utils/generateMapMarkers';
import { Store } from '~/lib/data-contract/salesforce';
import { useCheckoutState } from '~/features/checkout/hooks/useCheckoutState';
import { SearchInput } from '~/shared/components';
import { useTranslation } from '~/shared/utils/translation';
import { useIsMobile } from '~/shared/hooks/useIsMobile/useIsMobile';
import { default as Location } from '~/icons/location.svg';
import { StyledNoStoreFound, StyledUlListSelector } from './components/styled';
import { haversineDistance } from '~/shared/utils/haversineDistance';
import { useFilteredStores } from './hooks/useFilteredStores';

type Props = {
    storeList: Store[];
};

type ComponentProps = IM82FindDealerMapBlockViewModel & Props;

// Isolated search input component that manages its own state
const IsolatedSearchInput: FC<{
    placeholder: string;
    onDebouncedChange: (value: string) => void;
    resetTrigger: number;
}> = ({ placeholder, onDebouncedChange, resetTrigger }) => {
    const [localValue, setLocalValue] = useState('');

    // Reset when parent requests it
    useEffect(() => {
        if (resetTrigger > 0) {
            setLocalValue('');
        }
    }, [resetTrigger]);

    // Immediately notify parent of changes (no debouncing here)
    useEffect(() => {
        onDebouncedChange(localValue);
    }, [localValue, onDebouncedChange]);

    // Handle immediate input change - completely isolated
    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setLocalValue(e.target.value); // Instant local update
    }, []);

    return (
        <SearchInput
            onChange={handleChange}
            value={localValue}
            placeholder={placeholder}
            data-testid="searchZipCode"
        />
    );
};

export const StoreMapList: FC<ComponentProps> = ({ storeList }) => {
    const [map, setMap] = useState<google.maps.Map | null>(null);

    // Text-based filtering via hook
    const {
        filteredStores,
        filteredMapStores,
        setSearchQuery,
        isSearching,
        resetSearch,
        resetTrigger,
    } = useFilteredStores(storeList);

    // Location-based filtering (separate state)
    const [locationFilteredStores, setLocationFilteredStores] = useState<Store[]>([]);
    const [isLocationFilterActive, setIsLocationFilterActive] = useState(false);

    // Combined stores: Apply location filter to text-filtered results
    const displayStores = useMemo(() => {
        return isLocationFilterActive ? locationFilteredStores : filteredStores;
    }, [isLocationFilterActive, locationFilteredStores, filteredStores]);

    // Combined map stores: Apply location filter to text-filtered map results
    const displayMapStores = useMemo(() => {
        if (isLocationFilterActive) {
            // If location filter is active, use location filtered stores for map too
            return locationFilteredStores;
        }
        return filteredMapStores;
    }, [isLocationFilterActive, locationFilteredStores, filteredMapStores]);

    const { deliveryAddress: selectedPin, setDeliveryAddress } = useCheckoutState();
    const { translate } = useTranslation();
    const isMobile = useIsMobile();
    const { STORE_VIEW } = MapZoom;

    // Handle expensive map operations when display map stores change
    useEffect(() => {
        if (!map) return;

        const timeoutId = setTimeout(() => {
            if (!displayMapStores.length) return;

            // Only remove user marker if we're not in location filter mode
            if (!isLocationFilterActive) {
                removeUserMarker();
            }

            fitBounds(map, generateMapMarkers(displayMapStores), limit(displayMapStores));
            if (displayMapStores.length === 1) {
                setTimeout(() => {
                    map.setZoom(STORE_VIEW);
                }, 100);
            }
        }, 0);

        return () => clearTimeout(timeoutId);
    }, [displayMapStores, map, isLocationFilterActive]);

    const limit = useCallback(
        (stores: Store[]) => {
            return isMobile && stores.length < storeList.length ? 3 : stores.length;
        },
        [isMobile, storeList.length],
    );

    const mapCircle = useRef<google.maps.Circle | null>(null);
    const mapUserMarker = useRef<google.maps.Marker | null>(null);

    const removeUserMarker = () => {
        if (mapCircle.current && mapUserMarker.current && map) {
            mapCircle.current.setMap(null);
            mapUserMarker.current.setMap(null);

            mapCircle.current = null;
            mapUserMarker.current = null;
        }
    };

    const selectOptionStore = (storeOption: any) => {
        const clickLocal = {
            lat: storeOption.latitude || storeOption.lat,
            lng: storeOption.longitude || storeOption.lng,
        };

        if (map) {
            map.setZoom(15);
            map.panTo(clickLocal);
        }

        setDeliveryAddress(storeOption);
    };

    /**
     * When map is loaded, set it in state,
     * and if mapmarkers already are theye
     */
    const mapLoaded = useCallback(
        (map) => {
            setMap(map);
            if (storeList.length) {
                fitBounds(map, generateMapMarkers(storeList), limit(storeList));
            }
        },
        [storeList],
    );

    const handleFilterByDistance = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition((position: GeolocationPosition) => {
                const { coords } = position;
                const markerDistances: { [key: string]: number } = {};

                // Apply location filter to currently filtered stores (text + location)
                const currentStores = filteredStores;

                const filteredList = currentStores
                    .filter((marker) => {
                        const distance = haversineDistance(
                            {
                                latitude: coords.latitude,
                                longitude: coords.longitude,
                            },
                            {
                                latitude: marker?.latitude ?? 0,
                                longitude: marker?.longitude ?? 0,
                            },
                        );

                        const isNear = distance <= 10; //10km

                        if (isNear) {
                            markerDistances[marker.id] = distance;
                        }

                        return isNear;
                    })
                    .sort((a, b) => {
                        if (markerDistances[a.id] < markerDistances[b.id]) {
                            return -1;
                        } else if (markerDistances[a.id] > markerDistances[b.id]) {
                            return 1;
                        }

                        return 0;
                    });

                // Set location filtered stores and activate location filter
                setLocationFilteredStores(filteredList);
                setIsLocationFilterActive(true);

                if (filteredList.length && map) {
                    const userCenter = new google.maps.LatLng(coords.latitude, coords.longitude);

                    mapCircle.current = new google.maps.Circle({
                        strokeColor: '#F68A20',
                        strokeOpacity: 0.3,
                        strokeWeight: 2,
                        fillColor: '#F68A20',
                        fillOpacity: 0.1,
                        radius: 10000,
                        map,
                        center: userCenter,
                    });

                    mapUserMarker.current = new google.maps.Marker({
                        position: userCenter,
                        icon: {
                            url: markerUser,
                        },
                        map,
                    });

                    fitBounds(map, generateMapMarkers(filteredList), limit(filteredList));
                }
            });
        }
    };

    const handleResetList = () => {
        removeUserMarker();
        resetSearch(); // Reset text search
        setIsLocationFilterActive(false); // Reset location filter
        setLocationFilteredStores([]);

        if (storeList.length && map) {
            fitBounds(map, generateMapMarkers(storeList), limit(storeList));
        }
    };

    const mapCenter = useMemo(() => {
        // Return default center if no map or no stores
        if (!map || !displayStores.length) {
            return {
                lat: 55.970546, // Denmark Center location
                lng: 10.103119,
            };
        }

        const bounds = new google.maps.LatLngBounds();
        const locations = generateMapMarkers(displayStores);

        locations.forEach((m) => {
            const markerBounds = new google.maps.LatLng(m.lat, m.lng);
            bounds.extend(markerBounds);
        });

        return bounds.getCenter().toJSON();
    }, [displayStores, map]);

    const mapMarkerList = useMemo(() => generateMapMarkers(storeList), [storeList]);

    const isAnyFilterActive =
        (displayStores.length !== storeList.length && displayStores.length !== 0) ||
        isLocationFilterActive;

    const clearButtonLabel = isLocationFilterActive
        ? 'Clear Location'
        : translate('findStore.clear');

    return (
        <StyledContentMap>
            <StyledMapList>
                <>
                    <StyledSearchWrapper>
                        <IsolatedSearchInput
                            placeholder={translate('findStore.searchZipCode')}
                            onDebouncedChange={setSearchQuery}
                            resetTrigger={resetTrigger}
                        />

                        <StyledSearchLocation>
                            <StyledButton onClick={handleFilterByDistance}>
                                <Location />
                                {translate('findStore.closeToMyLocation')}
                            </StyledButton>

                            {/* Show clear button if ANY filtering is active */}
                            {isAnyFilterActive && (
                                <StyledButton onClick={handleResetList}>
                                    {clearButtonLabel}
                                </StyledButton>
                            )}
                        </StyledSearchLocation>
                    </StyledSearchWrapper>

                    {displayStores.length ? (
                        <StoreListUl
                            storeList={displayStores}
                            selectedPin={selectedPin}
                            selectOption={selectOptionStore}
                        />
                    ) : isSearching ? (
                        <StyledUlListSelector>
                            <StyledNoStoreFound>Searching...</StyledNoStoreFound>
                        </StyledUlListSelector>
                    ) : (
                        <StyledUlListSelector>
                            <StyledNoStoreFound>
                                {translate('findStore.noStoresFound')}

                                <StyledButton onClick={handleResetList}>
                                    {translate('findStore.clear')}
                                </StyledButton>
                            </StyledNoStoreFound>
                        </StyledUlListSelector>
                    )}
                </>
            </StyledMapList>

            <StyledMap data-testid="map">
                <MapHelper
                    markers={mapMarkerList}
                    markerIcon={{
                        url: markerOption,
                        anchor: map ? new google.maps.Point(29, 65) : null,
                    }}
                    selectedMarkerIcon={markerSelected}
                    onMapLoadCallback={mapLoaded}
                    onPinClick={selectOptionStore}
                    center={mapCenter}
                    clusterOptions={{
                        maxZoom: 12,
                        clusterClass: 'mapHelper__cluster',
                        averageCenter: true,
                        styles: [
                            {
                                textColor: '#000',
                                fontFamily: 'inherit',
                                url: '',
                                height: 40,
                                width: 40,
                            },
                        ],
                    }}
                    selectedPin={selectedPin}
                />
            </StyledMap>
        </StyledContentMap>
    );
};
